import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import {
  useLeaveCapacityList,
  useUpsertLeaveCapacity,
} from '@/products/leave/features/use-leave-admin';

export function LeaveAdminCapacityPage() {
  usePageTitle('Besetzung');
  const capacity = useLeaveCapacityList();
  const save = useUpsertLeaveCapacity();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function onSave(departmentId: string, current: number | null, event: FormEvent) {
    event.preventDefault();
    const raw = drafts[departmentId] ?? (current == null ? '' : String(current));
    const minPresent = Number(raw);
    if (!Number.isInteger(minPresent) || minPresent < 0) {
      setErrors((state) => ({
        ...state,
        [departmentId]: 'Bitte eine ganze Zahl ab 0 eingeben.',
      }));
      return;
    }
    setErrors((state) => {
      const next = { ...state };
      delete next[departmentId];
      return next;
    });
    try {
      await save.mutateAsync({ departmentId, body: { minPresent } });
      toast.success('Mindestbesetzung gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Mindestbesetzung konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Besetzung"
        description="Mindestanzahl anwesender Personen je Abteilung."
      />
      {capacity.isPending ? (
        <p className="text-sm text-muted-foreground">Abteilungen werden geladen …</p>
      ) : null}
      {capacity.isError ? (
        <p className="text-sm text-destructive">
          {capacity.error instanceof ApiRequestError
            ? capacity.error.message
            : 'Die Besetzung konnte nicht geladen werden.'}
        </p>
      ) : (
        <div className="space-y-4">
          {capacity.data?.items.map((item) => (
            <form
              key={item.departmentId}
              className={`flex flex-wrap items-end gap-3 ${leavePanelClassName}`}
              onSubmit={(event) => void onSave(item.departmentId, item.minPresent, event)}
            >
              <div className="min-w-48 flex-1">
                <p className="font-medium">{item.departmentName}</p>
                <Label htmlFor={`capacity-${item.departmentId}`} className="sr-only">
                  Mindestbesetzung
                </Label>
                <Input
                  id={`capacity-${item.departmentId}`}
                  className="mt-2"
                  placeholder="Mindestens anwesend"
                  value={
                    drafts[item.departmentId] ??
                    (item.minPresent == null ? '' : String(item.minPresent))
                  }
                  onChange={(event) =>
                    setDrafts((state) => ({ ...state, [item.departmentId]: event.target.value }))
                  }
                />
                {errors[item.departmentId] ? (
                  <p className="mt-1 text-sm text-destructive">{errors[item.departmentId]}</p>
                ) : null}
              </div>
              <Button type="submit" disabled={save.isPending}>
                Speichern
              </Button>
            </form>
          ))}
        </div>
      )}
    </div>
  );
}
